import styles from "./PersonModal.module.scss";
import FontAwesome from "../../utilities/FontAwesome";
import { UserAvatar } from "../../utilities/UserAvatar";
import { compressImage } from "../../../utils/DataUtilities";
import WatsonApi from "../../../backends/WatsonApi";
import NotificationManager from "../../notifications/NotificationManager";
import { isJson } from "../../admin/spinup/utilities";
import { useMachine } from "@xstate/react";
import { FetchMachine } from "../../machines/FetchMachine";
import { useStores } from "../../../stores/useStores";
import Cropper, { ReactCropperElement } from "react-cropper";
import { useRef, useState } from "react";
import { Box, Button } from "@radix-ui/themes";
import { dataURItoFile } from "../../../utils/MediaProcessor";

export function AvatarPicker(props: {
  blockObj: IPersonBlock;
  canEdit: boolean;
}) {
  const { organizationStore, userStore } = useStores();
  const [currentAvatarUpload, sendAvatarUpload] = useMachine(FetchMachine);

  const imageType = useRef<string | null>(null);
  const imageName = useRef<string | null>(null);
  const [cropperImage, setCropperImage] = useState("");
  const cropperRef = useRef<ReactCropperElement>(null);

  const personModel = props.blockObj.blockModel;

  async function handleAvatarUpload(file) {
    const userId = personModel.id;

    if (file.name.indexOf("'") !== -1) {
      const blob = file.slice(0, file.size, file.type);
      file = new File([blob], file.name.replace(/'/g, "%27"), {
        type: file.type,
        lastModified: file.lastModified,
      });
    }
    sendAvatarUpload("FETCH");
    // Try compressing file if the size is over 1MB
    try {
      file = await compressImage(file, 1);
      const client = await WatsonApi();
      const response =
        await client.apis.organizations.organizations_users_update_picture(
          {
            organization_pk: organizationStore.organization.id,
            id: userId,
            file,
          },
          {
            requestBody: {
              file,
            },
          },
        );
      const result = JSON.parse(response.data);
      if (result.avatar) {
        props.blockObj.setAttributes({
          blockModel: {
            ...props.blockObj.blockModel,
            avatarUrl: result.avatar,
          },
        });
        if (props.blockObj.blockModel.id === userStore.id) {
          userStore.setAvatar(result.avatar);
        }
        setCropperImage("");
        sendAvatarUpload("FULFILL");

        NotificationManager.success("Profile picture has been updated.");
      } else {
        sendAvatarUpload("REJECT");
        NotificationManager.error(result.error);
      }
    } catch (e) {
      sendAvatarUpload("REJECT");
      if (isJson(e.response?.data)) {
        NotificationManager.error(
          e.response?.body?.message,
          "Image Upload Error",
          4000,
        );
      } else {
        NotificationManager.error(e.message, undefined, 3000);
      }
    }
  }

  async function handleAvatarDelete() {
    sendAvatarUpload("FETCH");
    try {
      const client = await WatsonApi();
      const result =
        await client.apis.organizations.organizations_users_delete_picture({
          organization_pk: organizationStore.organization.id,
          id: personModel.id,
        });
      if (result["status"] === 200) {
        props.blockObj.setAttributes({
          blockModel: {
            ...props.blockObj.blockModel,
            avatarUrl: null,
          },
        });
        if (props.blockObj.blockModel.id === userStore.id) {
          userStore.setAvatar(null);
        }
        sendAvatarUpload("FULFILL");
        NotificationManager.success("Profile picture has been removed.");
      } else {
        throw new Error("Delete failed");
      }
    } catch {
      sendAvatarUpload("REJECT");
      NotificationManager.error("Error removing profile picture.");
    }
  }

  async function handleAvatarChange(event) {
    const files = event.target.files;

    if (!files.length) {
      return;
    }
    let file = files[0];

    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();

      img.onload = function () {
        setCropperImage(img.src);
      };

      img.src = reader.result as any;
    };

    imageType.current = file.type;
    imageName.current = file.name;
    reader.readAsDataURL(file);
  }

  return (
    <div>
      {cropperImage ? (
        <div className={"sb-teacher-image-cropper-container"}>
          <Cropper
            style={{ maxWidth: "100%" }}
            ref={cropperRef}
            src={cropperImage}
            aspectRatio={1}
            viewMode={2}
            autoCropArea={1}
            guides={false}
          />
          <Box pt={"1"} mr={"2"} display={"inline-block"}>
            <Button
              disabled={currentAvatarUpload.value === "PENDING"}
              onClick={() => {
                const cropper = cropperRef.current?.cropper;
                if (cropper) {
                  const canvas = cropper.getCroppedCanvas();
                  if (canvas) {
                    const imageFormat = imageType.current || "image/png";
                    const file = dataURItoFile(
                      canvas.toDataURL(imageFormat),
                      imageName.current,
                    );
                    handleAvatarUpload(file);
                  }
                }
              }}
            >
              Save
            </Button>
          </Box>
          <Box display={"inline-block"}>
            <Button
              disabled={currentAvatarUpload.value === "PENDING"}
              onClick={() => setCropperImage("")}
            >
              Cancel
            </Button>
          </Box>
        </div>
      ) : (
        <>
          <label
            htmlFor="input-teacher-avatar"
            id="teacher-img-placeholder"
            className="sb-teacher-img-placeholder"
            title={props.canEdit ? "Click to upload photo" : ""}
          >
            <div id="teacher-avatar-picture" className={styles.avatarContainer}>
              <UserAvatar
                avatarUrl={personModel.avatarUrl}
                fname={personModel.fname}
                lname={personModel.lname}
                size="9"
                avatarProps={{ className: styles.avatarImage }}
              />
              {props.canEdit && (
                <div className={styles.avatarOverlay}>
                  {currentAvatarUpload.value === "PENDING" ? (
                    <div className={styles.uploading}>
                      <div>
                        <FontAwesome
                          prefix={"fas"}
                          name={"fa-spinner"}
                          size={"3x"}
                          spin
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="sb-teacher-select-img-container">
                      <FontAwesome prefix={"fas"} name={"fa-camera"} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </label>
          {props.canEdit && (
            <>
              <input
                type="file"
                className="hidden"
                name="avatar"
                id="input-teacher-avatar"
                onChange={handleAvatarChange}
                accept={"image/*"}
              />
              {personModel.avatarUrl && (
                <Box pt={"2"} className={styles.deleteButton}>
                  <Button
                    variant="soft"
                    color="red"
                    size="1"
                    disabled={currentAvatarUpload.value === "PENDING"}
                    onClick={handleAvatarDelete}
                  >
                    <FontAwesome prefix={"fas"} name={"fa-trash"} />
                    Remove Photo
                  </Button>
                </Box>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
